import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { enableMapSet } from "immer";
import { persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import autosaveMiddlewareV2 from "./middleware/autosaveMiddlewareV2";
import filterPersistMiddleware from "./middleware/filterPersistMiddleware";

// Import reducers
import aufmassV2Reducer from "./slices/aufmassV2Slice";
import authReducer, {
	clearAuth,
	logout,
	verifyOtp,
} from "./slices/authSlice";
import boqReducer from "./slices/boqSlice";
import companyReducer from "./slices/companySlice";
import costTrackingReducer from "./slices/costTrackingSlice";
import invoiceV2Reducer from "./slices/invoiceV2Slice";
import projectDetailsReducer from "./slices/projectDetailsSlice";
import projectLVReducer from "./slices/projectLVSlice";
import sharedFilterReducer from "./slices/sharedFilterSlice";
import supplierInfoReducer from "./slices/supplierInfoSlice";
import templateReducer from "./slices/templateSlice";
import userReducer from "./slices/userSlice";
import { baseApi } from "./services/api";
import "./services/projectPermissionPresets";
import "./services/adminPermissionTemplates";
import "./services/adminProjects";
import "./services/adminUsers";

// Enable Map and Set support for Immer
enableMapSet();

const persistConfig = {
	key: "root",
	storage,
	whitelist: ["auth", "sharedFilters"],
};

const appReducer = combineReducers({
	// auth slices
	auth: authReducer,

	// settings slices
	users: userReducer,
	companies: companyReducer,
	templates: templateReducer,

	// project slices
	projectDetails: projectDetailsReducer,
	aufmassV2: aufmassV2Reducer,
	invoiceV2: invoiceV2Reducer,
	supplierInfo: supplierInfoReducer,
	projectLV: projectLVReducer,
	boq: boqReducer,
	costTracking: costTrackingReducer,

	// shared slices
	sharedFilters: sharedFilterReducer,

	[baseApi.reducerPath]: baseApi.reducer,
});

// Session boundaries: a forced 401 clear, an explicit logout, or a fresh login.
// On any of these we drop the RTK Query cache (the `api` slice) so a user can
// never see responses cached during a previous session - e.g. logging out and
// back in as a different user on the same tab. The `api` slice is in-memory
// only (not in the persist whitelist), so resetting it here is sufficient.
const SESSION_BOUNDARY_ACTIONS = new Set([
	clearAuth.type,
	logout.fulfilled.type,
	logout.rejected.type,
	verifyOtp.fulfilled.type,
]);

const rootReducer = (state, action) => {
	if (state && SESSION_BOUNDARY_ACTIONS.has(action.type)) {
		state = { ...state, [baseApi.reducerPath]: undefined };
	}
	return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [
					"persist/PERSIST",
					// V2 actions: these carry Map payloads or mutate invoiceV2.invoicePositions
					// (a Map<string, positionData>). Redux Toolkit's serializability check
					// cannot inspect Map values, so each action that touches the Map must be
					// listed here. Add new Map-mutating actions to this list or the DevTools
					// middleware will log warnings and may drop state in development.
					"invoiceV2/autosaveInvoiceV2",
					"invoiceV2/setFormField",
					"invoiceV2/setInvoicePositionQuantity",
					"invoiceV2/setInvoicePositionComments",
					"invoiceV2/setPosition",
				],
				ignoredPaths: [
					// V2 formState date fields hold Date objects (DatePickerAtom
					// needs real Dates, and the reducer stores `new Date(...)`).
					// These live in non-persisted state, so it is safe to skip the
					// serializability check for them.
					"invoiceV2.formState.invoiceDate",
					"invoiceV2.formState.invoiceArrivalDate",
					"invoiceV2.formState.orderDate",
					"invoiceV2.formState.cashDiscountDate",
					"invoiceV2.formState.billingPeriodFrom",
					"invoiceV2.formState.billingPeriodTo",
					// V2 Map-based state (not serializable but handled by Immer)
					"invoiceV2.invoicePositions",
				],
			},
		}).concat(
			baseApi.middleware,
			autosaveMiddlewareV2,
			filterPersistMiddleware,
		),
	devTools: process.env.NODE_ENV !== "production",
});

export const persistor = persistStore(store);
